import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CoinGrid } from "@/components/coins/CoinGrid";
import { Button } from "@/components/ui/button";
import { Flame, Clock, TrendingUp, Sparkles } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

type FilterType = "trending" | "new" | "market-cap" | "premium";

const filters: { id: FilterType; label: string; icon: React.ElementType }[] = [
  { id: "trending", label: "Trending", icon: Flame },
  { id: "new", label: "New", icon: Clock },
  { id: "market-cap", label: "Market Cap", icon: TrendingUp },
  { id: "premium", label: "Premium", icon: Sparkles },
];

export function TrendingSection() {
  const [activeFilter, setActiveFilter] = useState<FilterType>("trending");

  const { data: coins, isLoading } = useQuery({
    queryKey: ["coins", activeFilter],
    queryFn: async () => {
      let query = supabase
        .from("coins")
        .select("*")
        .limit(12);

      switch (activeFilter) {
        case "trending":
          query = query.order("market_cap", { ascending: false });
          break;
        case "new":
          query = query.order("created_at", { ascending: false });
          break;
        case "market-cap":
          query = query.order("market_cap", { ascending: false });
          break;
        case "premium":
          query = query.eq("is_premium", true).order("created_at", { ascending: false });
          break;
      }

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
  });

  return (
    <section className="py-8">
      <div className="container">
        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="font-display text-2xl font-bold text-foreground mb-1">
              Explore Coins
            </h2>
            <p className="text-sm text-muted-foreground">
              Discover the hottest meme coins on Solana
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
            {filters.map((filter) => {
              const Icon = filter.icon;
              return (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all",
                    activeFilter === filter.id
                      ? "bg-primary text-primary-foreground glow-primary"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {filter.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Coin Grid */}
        <CoinGrid coins={coins ?? []} isLoading={isLoading} />

        {/* View All Button */}
        {coins && coins.length > 0 && (
          <div className="flex justify-center mt-8">
            <Button variant="outline" size="lg" asChild>
              <Link to="/trending">View All Coins</Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
